import { useEffect, useState, useRef } from "react";

type Status = {
  state: "open" | "lunch" | "closed";
  label: string;
  shortLabel: string;
};

// Öffnungszeiten: Mo-Do 09:00-12:30 & 14:00-16:30, Fr 09:00-14:00
const HOURS: Record<number, Array<[number, number]>> = {
  // 0 = Sonntag ... 6 = Samstag
  1: [[9 * 60, 12 * 60 + 30], [14 * 60, 16 * 60 + 30]],
  2: [[9 * 60, 12 * 60 + 30], [14 * 60, 16 * 60 + 30]],
  3: [[9 * 60, 12 * 60 + 30], [14 * 60, 16 * 60 + 30]],
  4: [[9 * 60, 12 * 60 + 30], [14 * 60, 16 * 60 + 30]],
  5: [[9 * 60, 14 * 60]],
};

const DAY_NAMES = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function fmt(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function getBerlinNow(): { day: number; minutes: number } {
  // Lokalzeit in Berlin via Intl-Parts (robuster als toLocaleString-Parsing)
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const wd = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
  const hh = parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10);
  const mm = parseInt(parts.find((p) => p.type === "minute")?.value ?? "0", 10);
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return { day: map[wd] ?? 1, minutes: hh * 60 + mm };
}

function computeStatus(): Status {
  const { day, minutes } = getBerlinNow();
  const today = HOURS[day];

  if (today) {
    for (const [start, end] of today) {
      if (minutes >= start && minutes < end) {
        return { state: "open", label: "Jetzt erreichbar", shortLabel: "Erreichbar" };
      }
    }
    // Heute, aber vor erstem Slot? -> "Geöffnet ab HH:MM"
    const nextToday = today.find(([start]) => minutes < start);
    if (nextToday) {
      // Mo–Do Mittagspause = orange, sonst grau (vor erstem Slot heute)
      const isLunch =
        day >= 1 && day <= 4 && minutes >= today[0][1] && minutes < today[1][0];
      const label = `Geöffnet ab ${fmt(nextToday[0])}`;
      return {
        state: isLunch ? "lunch" : "closed",
        label,
        shortLabel: "Geschlossen",
      };
    }
  }

  // Nächsten Öffnungstag finden
  for (let i = 1; i <= 7; i++) {
    const d = (day + i) % 7;
    const slots = HOURS[d];
    if (slots) {
      const prefix = i === 1 ? "morgen" : DAY_NAMES[d];
      return {
        state: "closed",
        label: `Geöffnet ab ${prefix} ${fmt(slots[0][0])}`,
        shortLabel: "Geschlossen",
      };
    }
  }
  return { state: "closed", label: "Geschlossen", shortLabel: "Geschlossen" };
}

export function useAvailabilityStatus(): Status | null {
  const [status, setStatus] = useState<Status | null>(() => {
    try {
      return computeStatus();
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const id = window.setInterval(() => {
      try {
        setStatus(computeStatus());
      } catch {
        /* no-op */
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  return status;
}

interface Props {
  /** "light" = für dunklen Hintergrund (transparente Navbar), "dark" = für hellen Hintergrund. */
  variant?: "light" | "dark";
  className?: string;
}

export default function LiveStatusBadge({ variant = "dark", className = "" }: Props) {
  const status = useAvailabilityStatus();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const isLight = variant === "light";
  const textColor = isLight ? "text-white/90" : "text-foreground-700";

  // Fallback: nur grauer Dot ohne Text, Navbar nicht zerschießen
  if (!status) {
    return (
      <div className={`inline-flex items-center ${className}`} aria-hidden="true">
        <span className="w-2 h-2 rounded-full bg-foreground-400 inline-block" />
      </div>
    );
  }

  const dotColor =
    status.state === "open" ? "#4CAF50" : status.state === "lunch" ? "#F59E0B" : "#9CA3AF";

  return (
    <div ref={ref} className={`relative inline-flex flex-col items-start ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => {
          if (window.matchMedia("(hover: hover)").matches) setOpen(true);
        }}
        onMouseLeave={() => {
          if (window.matchMedia("(hover: hover)").matches) setOpen(false);
        }}
        aria-label={`Erreichbarkeit: ${status.label}. Öffnungszeiten anzeigen.`}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-sans text-[12px] leading-none ${textColor} hover:opacity-90 transition-opacity`}
      >
        <span className="relative inline-flex w-2 h-2 shrink-0">
          {status.state === "open" && (
            <span
              className="absolute inset-0 rounded-full opacity-60 motion-safe:animate-ping motion-reduce:hidden"
              style={{ backgroundColor: dotColor }}
              aria-hidden="true"
            />
          )}
          <span
            className="relative inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: dotColor }}
          />
        </span>
        <span className="hidden xl:inline whitespace-nowrap">{status.label}</span>
        <span className="hidden md:inline xl:hidden whitespace-nowrap">{status.shortLabel}</span>
      </button>

      {/* Mobile: Öffnungszeiten immer sichtbar unter dem Badge, kein Hover/Tooltip */}
      <div className={`md:hidden mt-2 text-[11px] leading-snug font-sans ${isLight ? "text-white/80" : "text-foreground-600"}`}>
        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
          <span className={isLight ? "text-white/60" : "text-foreground-500"}>Mo–Do</span>
          <span>09:00–12:30 · 14:00–16:30</span>
          <span className={isLight ? "text-white/60" : "text-foreground-500"}>Fr</span>
          <span>09:00–14:00</span>
          <span className={isLight ? "text-white/60" : "text-foreground-500"}>Sa/So</span>
          <span>geschlossen</span>
        </div>
      </div>

      {/* Desktop: Tooltip bei Hover/Klick */}
      {open && (
        <div
          role="tooltip"
          className="hidden md:block absolute top-full left-0 sm:left-auto sm:right-0 mt-2 z-50 min-w-[230px] max-w-[calc(100vw-2rem)] rounded-lg bg-[#0A0A0A] text-white text-xs px-3.5 py-3 shadow-xl animate-fade-in"
        >
          <p className="font-semibold mb-1.5 text-[12px] tracking-wide uppercase text-white/80">
            Öffnungszeiten
          </p>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-sans">
            <span className="text-white/60">Mo–Do</span>
            <span>09:00–12:30 · 14:00–16:30</span>
            <span className="text-white/60">Fr</span>
            <span>09:00–14:00</span>
            <span className="text-white/60">Sa/So</span>
            <span>geschlossen</span>
          </div>
        </div>
      )}
    </div>
  );
}

